import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import cn from 'classnames'
import { ProductStockStatus } from '@pylot-data/enums/ProductStockStatus.d'
import { MinusIcon, PlusIcon, Trash } from '@components/icons'
import s from './AddToCartBtn.module.scss'
import {
    BundleProduct,
    CartItemInterface,
    ConfigurableProduct,
    ConfigurableCartItem
} from '@pylot-data/pylotschema'
import {
    CartData,
    useCart,
    useRemoveItemFromCart,
    useUpdateCartItems
} from '@corratech/pylot-cart-manager'
import { useAddToCart } from 'stores/ooka_usa_store/components/alfakher/pylot-cart-manager/src/use-add-to-cart'
import { ButtonLabel } from '@pylot-data/hooks/product/use-product-ui'
import { getCartItemSplited } from '@components/common/CartItemUtil/CartItemUtil'
import router, { useRouter } from 'next/router'
import { getPDPUrl } from '@lib/getPDPUrl'
import { ProductType } from '@pylot-data/hooks/product/use-product-fetch'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'

type TAddToCartBtn = {
    inViewport: boolean
    product: ConfigurableProduct | BundleProduct
    selectedOptionUIDs: string[]
    wrapperClasses?: string
    onResponse: (response: CartData | undefined) => void
    onError?: (message: string) => void
    gtmLocation?: string
    isPlp?: boolean
}

const AddToCartBtn: (props: TAddToCartBtn) => JSX.Element = ({
    inViewport,
    product,
    selectedOptionUIDs = [],
    wrapperClasses,
    onResponse,
    onError,
    gtmLocation,
    isPlp
}: TAddToCartBtn) => {
    const [cart, setCart] = useState<CartItemInterface | undefined>()
    const { t } = useTranslation('common')
    const { data: cartData } = useCart()
    const { addToCart, isAdding } = useAddToCart()

    const {
        updateCartItems,
        isLoading: isCartUpdating,
        errorMessage
    } = useUpdateCartItems()
    const {
        removeItemFromCart,
        isLoading: isRemoving
    } = useRemoveItemFromCart()

    // @ts-ignore
    const { setGTMLocation } = useAlfakherContext()

    const productUrl = `/${getPDPUrl(product?.url_key as string)}`
    const cartItems = cartData?.data?.cart?.items
    const { freeCartItem, paidCartItem } = getCartItemSplited(
        cartItems || [],
        cartData?.data?.cart?.free_gifts_skus
    )

    useEffect(() => {
        const cartItem = cartItems?.find(
            (item) => item?.product?.id === product.id
        )
        setCart(cartItem || undefined)
    }, [cartItems, product])

    useEffect(() => {
        if (errorMessage && onError) {
            onError(errorMessage)
        }
    }, [errorMessage, onError])

    const out_of_stock = product.stock_status == ProductStockStatus.OutOfStock
    const isConfigurableProduct =
        product.__typename === ProductType.CONFIGURABLE_PRODUCT
    const isBundle = product.__typename === ProductType.BUNDLE_PRODUCT
    const getButtonLabel = () => {
        if (out_of_stock) return t('SOLD OUT')
        if (isBundle) {
            if ((product as BundleProduct)?.is_custom_kit) return t('Customize')
            if (isPlp && !(product as BundleProduct)?.is_bundle_options_hidden)
                return t('Customize')
        }
        return t(ButtonLabel.ADD_TO_CART)
    }

    const RenderedButtonLabel = () => {
        if (isAdding || isCartUpdating || isRemoving) {
            return <div>{t('Loading...')}</div>
        }
        if (productCart?.quantity) {
            return (
                <div className={s['content-wrapper']}>
                    <DecreaseIcon />
                    <span>{productCart.quantity}</span>
                    {productCart?.quantity < 999 ? (
                        <IncreaseIcon />
                    ) : (
                        <span className={s['icons']} />
                    )}
                </div>
            )
        }
        return <div className={s['label']}>{getButtonLabel()}</div>
    }

    const handleAddToCart = async () => {
        setGTMLocation(gtmLocation)
        const finalSelectedOptions = [...selectedOptionUIDs]
        if (isBundle && (product as BundleProduct)?.is_bundle_options_hidden) {
            ;(product as BundleProduct).items?.forEach((item) => {
                const firstOptionUid = item?.options?.[0]?.uid
                if (firstOptionUid) {
                    finalSelectedOptions.push(firstOptionUid)
                }
            })
        }

        const response = await addToCart(
            [
                {
                    sku: product.sku as string,
                    selected_options: finalSelectedOptions,
                    quantity: 1
                }
            ],
            [product]
        )
        onResponse(response)

        const apiErrors = response?.errors ?? response?.user_errors
        if (apiErrors?.length && onError) {
            // Use first error message for the tile-level toast
            const firstMsg = apiErrors[0]?.message
            if (firstMsg) {
                onError(firstMsg)
            }
        }
    }
    const productCart = isConfigurableProduct
        ? [...freeCartItem, ...paidCartItem]?.find((item) => {
              return ((item as unknown) as ConfigurableCartItem)?.configurable_options?.find(
                  (opt) =>
                      opt!.configurable_product_option_value_uid ===
                      selectedOptionUIDs[0]
              )
          })
        : cart

    const handleButtonClick = () => {
        if (out_of_stock) return
        if (isBundle) {
            if ((product as BundleProduct)?.is_custom_kit) {
                const currentQuery = { ...router.query }
                currentQuery.option = 'customize'

                router.push(
                    {
                        pathname: productUrl,
                        query: currentQuery
                    },
                    undefined,
                    { shallow: true }
                )
                return
            } else if (
                isPlp &&
                !(product as BundleProduct)?.is_bundle_options_hidden
            ) {
                router.push({
                    query: { ...router.query },
                    pathname: productUrl
                })
                return
            }
        }
        handleAddToCart()
    }

    const DecreaseIcon = () => {
        const _handleDecrease = async (e: React.MouseEvent) => {
            e.stopPropagation()
            updateCartItems([
                {
                    cart_item_uid: productCart?.uid as string,
                    quantity: productCart?.quantity! - 1
                }
            ])
        }
        const _handleDelete = (e: React.MouseEvent) => {
            e.stopPropagation()
            removeItemFromCart(productCart?.uid as string)
        }
        return productCart?.quantity! > 1 ? (
            // eslint-disable-next-line
            <div className={`${s['icons']} py-3`} onClick={_handleDecrease}>
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <MinusIcon fill="#fff" />
            </div>
        ) : (
            <div className={s['icons']}>
                <Trash onClick={_handleDelete} />
            </div>
        )
    }

    const IncreaseIcon = () => {
        const _handleIncrease = (e: React.MouseEvent) => {
            e.stopPropagation()
            setGTMLocation(gtmLocation)
            updateCartItems([
                {
                    cart_item_uid: productCart?.uid as string,
                    quantity: productCart?.quantity! + 1
                }
            ])
        }
        return (
            <div className={s['icons']}>
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <PlusIcon fill="#fff" onClick={_handleIncrease} />
            </div>
        )
    }

    const buttonClasses = cn([
        s.root,
        'btn-primary',
        wrapperClasses,
        {
            'add-to-cart-button-floating': !inViewport
        }
    ])

    return (
        <button
            aria-label={t('Add to Cart')}
            type="button"
            className={buttonClasses}
            disabled={isAdding || out_of_stock || isCartUpdating || isRemoving}
            onClick={handleButtonClick}
        >
            <RenderedButtonLabel />
        </button>
    )
}

export default AddToCartBtn
